/**
 * Search and Discovery Configuration
 * 
 * Centralized configuration for meal search radius and filtering.
 * Update DEFAULT_RADIUS_MILES to change the search radius across the entire app.
 */

export const SEARCH_CONFIG = {
  /**
   * Default search radius in miles
   * Used for filtering meals in Firestore queries
   * Update this value to change the radius across all search locations
   */
  DEFAULT_RADIUS_MILES: 10,

  /**
   * Default search radius in meters
   * Used for Algolia geographic filtering (aroundRadius parameter)
   * Automatically calculated from DEFAULT_RADIUS_MILES
   */
  DEFAULT_RADIUS_METERS: 16093, // 10 miles in meters

  /**
   * Maximum allowed search radius in miles
   * Can be used for future radius selector feature
   */
  MAX_RADIUS_MILES: 50,

  /**
   * Maximum allowed search radius in meters
   */
  MAX_RADIUS_METERS: 80467, // 50 miles in meters

  /**
   * Predefined radius options for user selection
   * Can be used in a future radius selector UI component
   */
  RADIUS_OPTIONS: [5, 10, 25, 50] as const,
};

/**
 * Convert miles to meters for Algolia aroundRadius parameter
 * @param miles - Distance in miles
 * @returns Distance in meters, rounded to nearest integer
 */
export const milesToMeters = (miles: number): number => {
  return Math.round(miles * 1609.34);
};

/**
 * Convert meters to miles for display purposes
 * @param meters - Distance in meters
 * @returns Distance in miles
 */
export const metersToMiles = (meters: number): number => {
  return meters / 1609.34;
};

/**
 * Get the current search radius in miles
 * @returns Current default radius in miles
 */
export const getSearchRadiusMiles = (): number => {
  return SEARCH_CONFIG.DEFAULT_RADIUS_MILES;
};

/**
 * Get the current search radius in meters
 * @returns Current default radius in meters for Algolia
 */
export const getSearchRadiusMeters = (): number => {
  return SEARCH_CONFIG.DEFAULT_RADIUS_METERS;
};
